/**
 * ST-01 Contrastive framing (INV-CH-ST-01; changes C-D1-ST01, C-D2-ST01).
 *
 * Events: CONTRAST_RES hits ("not just X, but Y", "it's not X — it's Y", …; 27 regexes, each counted
 * as in the seed, so one construction matched by two regexes counts twice) per 1k prose words.
 * The seed's raw-count ramp is removed (D1). One-way channel: the seed's own zero guard (score 10 at
 * zero events) is prepended to the rate ramp as (0, 10) (D2).
 */
import {
	compiledEnglish,
	line,
	nativeMeasure,
	proseBlocks,
	quote,
	rateDef,
	wordExposure,
} from './a-shared.ts';
import { makeEvidence, rateChannel } from './framework.ts';
import type { ChannelImpl, Measurement, RunContext } from './types.ts';

export const st01Def = rateDef({
	id: 'ST-01',
	name: 'Contrastive framing',
	family: 'RHETORIC',
	features: [{ id: 'contrast', label: 'Contrastive frame' }],
	oneWay: true,
	minExposure: 300,
	exposureUnit: 'words',
	lang: 'native',
	ramp: [
		[0, 10],
		[0.5, 38],
		[1.5, 58],
		[3, 78],
		[6, 93],
	],
	unitScale: 1000,
	unitLabel: 'constructions per 1k words',
	phi0: 1.5,
	weight: 1.9,
	nd: 0.5,
	changes: ['C-D1-ST01', 'C-D2-ST01'],
});

function measure(ctx: RunContext): Measurement {
	const { doc } = ctx;
	const lex = compiledEnglish();
	const k = new Float64Array(doc.paragraphs.count);
	const e = wordExposure(doc);
	const ev = makeEvidence();
	const summary: string[] = [];
	for (const b of proseBlocks(doc)) {
		lex.contrast.scanBlock(b.text, b.base, (s, en, _i, m) => {
			k[b.p] = (k[b.p] as number) + 1;
			ev.add(s, en, 0, 1);
			if (summary.length < 10)
				summary.push(
					line(quote(m[0].slice(0, 80) + (m[0].length > 80 ? '…' : '')), 'contrastive frame')
				);
		});
	}
	return { k, e, evidence: ev, summary };
}

export const st01: ChannelImpl = rateChannel(st01Def, nativeMeasure(measure));
export { st01Def as def, st01 as impl };
